import React, { useMemo } from 'react';
import type { InvestmentIndicatorBarData, InvestmentIndicatorCardData } from '@app-types/investmentIndicators';
import { getIndicatorBarGeometry } from './investmentIndicatorBarLayout';
import { useInvestmentIndicatorBarLayout } from './useInvestmentIndicatorBarLayout';
import './InvestmentIndicatorCard.less';

export interface InvestmentIndicatorCardProps {
  data: InvestmentIndicatorCardData;
}

interface IndicatorBarProps {
  bar: InvestmentIndicatorBarData;
  role: 'company' | 'industry';
  card: InvestmentIndicatorCardData;
}

const IndicatorBar: React.FC<IndicatorBarProps> = ({ bar, role, card }) => {
  const { scaleMode, minValue, maxValue } = card;
  const geometry = useMemo(
    () => getIndicatorBarGeometry(scaleMode, bar.value, minValue, maxValue),
    [scaleMode, bar.value, minValue, maxValue],
  );
  const { plotRef, labelRef, layout } = useInvestmentIndicatorBarLayout(geometry, bar.display);
  // Until a hidden/unmeasured track can be measured, keep the full text readable.
  const mode = layout?.mode ?? 'line';

  return (
    <div className="investment-indicator-card__bar-block">
      <div className={`investment-indicator-card__bar-label investment-indicator-card__bar-label--${role}`}>
        {bar.label}
      </div>
      <div className={`investment-indicator-card__bar-track${mode === 'line' ? ' investment-indicator-card__bar-track--label-line' : ''}`}>
        <div className="investment-indicator-card__bar-plot" ref={plotRef}>
          {geometry && geometry.width > 0 && (
            <div
              className={`investment-indicator-card__bar-fill investment-indicator-card__bar-fill--${role}`}
              style={{ left: `${geometry.left * 100}%`, width: `${geometry.width * 100}%` }}
              aria-hidden="true"
            />
          )}
        </div>
        <span
          ref={labelRef}
          className={`investment-indicator-card__bar-value investment-indicator-card__bar-value--${role} investment-indicator-card__bar-value--${mode}`}
          style={mode === 'line' ? undefined : { left: layout?.left }}
        >
          {bar.display}
        </span>
      </div>
    </div>
  );
};

/**
 * Single investment indicator card (PBR / PER / ROE / ROA).
 * Figma reference: card 3169:8238 (web).
 * Layout: header (label + value) → company bar → industry bar → min/max scale.
 * Uses CSS-only horizontal bars — no ECharts.
 */
const InvestmentIndicatorCard: React.FC<InvestmentIndicatorCardProps> = ({ data }) => {
  const rootClassName =
    data.scaleMode === 'centered-zero'
      ? 'investment-indicator-card investment-indicator-card--centered'
      : 'investment-indicator-card';

  return (
    <article className={rootClassName} data-node-id={data.nodeIdWeb}>
      <div className="investment-indicator-card__header">
        <span className="investment-indicator-card__label">{data.label}</span>
        <span className="investment-indicator-card__value">{data.valueDisplay}</span>
      </div>

      <IndicatorBar bar={data.company} role="company" card={data} />
      <IndicatorBar bar={data.industry} role="industry" card={data} />

      {/* Min/max scale row */}
      <div className="investment-indicator-card__scale">
        <span>{data.minLabel}</span>
        <span>{data.maxLabel}</span>
      </div>
    </article>
  );
};

export default InvestmentIndicatorCard;
